import { Box, Card, Skeleton, Switch, Typography } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import React, { useCallback, useEffect, useState } from 'react';

import type { DailyDashboardIssue } from '../../../backendsdk/api';
import { TrackedLink as Link } from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import AccidentIssueCard from './AccidentIssueCard';
import CoachIssueCard from './CoachIssueCard';
import { DAILYDASHBOARD_MIXPANEL_SESSION, FleetDriversView } from './Defs';
import PremiumOptimizationPageHeader from './PageHeader';
import WellnessIssueCard from './WellnessIssueCard';

const DailyDashboard: React.FC = () => {
    const [viewMode, setViewMode] = useState<FleetDriversView>('vehicles');
    const [items, setItems] = useState<DailyDashboardIssue[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const { api } = useApi();

    useEffect(() => {
        mixpanel.track('daily-dashboard', {
            name: 'page enter',
            session_id: DAILYDASHBOARD_MIXPANEL_SESSION,
        });

        return () => {
            mixpanel.track('daily-dashboard', {
                name: 'page left',
                session_id: DAILYDASHBOARD_MIXPANEL_SESSION,
            });
        };
    }, []);

    const loadData = useCallback(() => {
        setIsLoading(true);
        setIsError(false);
        api.apiV0DailyDashboardGet()
            .then((res) => {
                const raw = res.data || [];
                const dataItems = raw.map((item) => {
                    const details = item.issue_details ?? {};
                    const issue = Array.isArray(details.issue)
                        ? (details.issue as unknown[]).filter((x) => typeof x === 'string')
                        : [];
                    const remediation = Array.isArray(details.remediation)
                        ? (details.remediation as unknown[]).filter((x) => typeof x === 'string')
                        : [];
                    return {
                        ...item,
                        issue_details: { ...details, issue, remediation },
                    };
                });
                setItems(dataItems);
                setIsLoading(false);
                mixpanel.track('daily-dashboard', {
                    name: 'page loaded',
                    session_id: DAILYDASHBOARD_MIXPANEL_SESSION,
                });
            })
            .catch(() => {
                setIsError(true);
                setIsLoading(false);
            });
    }, [api]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleViewModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setViewMode(event.target.checked ? 'drivers' : 'vehicles');
    };

    const accidentItems = items.filter((item) => item.issue_type === 'ACCIDENT');
    const coachItems = items.filter((item) => item.issue_type === 'COACHING');
    const coachItemsOffset = accidentItems.length;
    const wellnessItems = items.filter((item) => item.issue_type === 'WELLNESS');
    const wellnessItemsOffset = coachItems.length + coachItemsOffset;

    if (isLoading) {
        return (
            <Box sx={{ height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 5, minHeight: '100%' }}>
                    <PremiumOptimizationPageHeader />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            py: 3,
                            width: '100%',
                        }}
                    >
                        <Skeleton variant="text" sx={{ fontSize: 30, width: 160, mb: 1, mt: 6 }} />
                        <Card sx={{ width: 'clamp(40%, 700px, 80%)', borderRadius: 2 }}>
                            <Skeleton variant="rounded" height={150} animation="wave" />
                        </Card>
                        <Skeleton variant="text" sx={{ fontSize: 30, width: 160, my: 1 }} />
                        <Card sx={{ width: 'clamp(40%, 700px, 80%)', borderRadius: 2 }}>
                            <Skeleton variant="rounded" height={200} animation="wave" />
                        </Card>
                        <Card sx={{ width: 'clamp(40%, 700px, 80%)', borderRadius: 2 }}>
                            <Skeleton variant="rounded" height={180} animation="wave" />
                        </Card>
                    </Box>
                </Box>
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 5, minHeight: '100%' }}>
                    <PremiumOptimizationPageHeader />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 6,
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            Failed to load daily dashboard data.
                        </Typography>
                        <Link
                            id="daily-dashboard-reload-data"
                            component="button"
                            variant="body2"
                            onClick={() => loadData()}
                            sx={{ textDecoration: 'underline' }}
                        >
                            Reload data
                        </Link>
                    </Box>
                </Box>
            </Box>
        );
    }

    if (items.length === 0) {
        return (
            <Box sx={{ height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 5, minHeight: '100%' }}>
                    <PremiumOptimizationPageHeader />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 6,
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            No issues or recommendations at this time.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 5, minHeight: '100%' }}>
                <PremiumOptimizationPageHeader />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        py: 3,
                        width: '100%',
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: '80%' }}>
                        Use this switch to toggle between issues and recommendations across your entire fleet or for
                        individual drivers.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography
                            variant="body1"
                            component="span"
                            sx={{ fontWeight: viewMode === 'vehicles' ? 700 : 400 }}
                        >
                            VEHICLES
                        </Typography>
                        <Switch
                            checked={viewMode === 'drivers'}
                            onChange={handleViewModeChange}
                            color="default"
                            inputProps={{ 'aria-label': 'Fleet or drivers view' }}
                        />
                        <Typography
                            variant="body1"
                            component="span"
                            sx={{ fontWeight: viewMode === 'drivers' ? 700 : 400 }}
                        >
                            DRIVERS
                        </Typography>
                    </Box>
                </Box>
                {accidentItems.length > 0 ? (
                    <Typography variant="overline" sx={{ fontSize: 18, fontWeight: 'lighter' }}>
                        Accidents
                    </Typography>
                ) : null}
                {accidentItems.map((item, index) => {
                    return <AccidentIssueCard key={index} item={item} cardId={index + 1} viewMode={viewMode} />;
                })}
                {coachItems.length > 0 ? (
                    <Typography variant="overline" sx={{ fontSize: 18, fontWeight: 'lighter' }}>
                        Coaching Sessions
                    </Typography>
                ) : null}
                {coachItems.map((item, index) => {
                    return (
                        <CoachIssueCard
                            key={index}
                            item={item}
                            cardId={index + coachItemsOffset + 1}
                            viewMode={viewMode}
                        />
                    );
                })}
                {wellnessItems.length > 0 ? (
                    <Typography variant="overline" sx={{ fontSize: 18, fontWeight: 'lighter' }}>
                        Wellness
                    </Typography>
                ) : null}
                {wellnessItems.map((item, index) => {
                    return (
                        <WellnessIssueCard
                            key={index}
                            item={item}
                            cardId={index + wellnessItemsOffset + 1}
                            viewMode={viewMode}
                        />
                    );
                })}
            </Box>
        </Box>
    );
};

export default DailyDashboard;
